import config from '@/config'
import Conversations, { ConversationType } from '@/models/Conversation'
import { useGlobalStore } from '@/store/app'
import { PaginationParams } from '@/types/PaginationParams'
import BaseService from './BaseService'
import { requests } from './HTTPService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('locationId')
class ConversationsServiceCls extends BaseService<Conversations> {
  constructor() {
    super(null as any)
  }

  model() {
    return Conversations
  }

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/conversations`
  }

  async createConversation(
    members: Array<string>,
    type: ConversationType,
    skipFetchContactId?: boolean
  ): Promise<any> {
    const url = skipFetchContactId
      ? `${this.endpoint}?skipFetchingContactId=${skipFetchContactId}`
      : `${this.endpoint}`
    return http
      .post(url, {
        members,
        type,
      })
      .then((res: any) => {
        return res.data
      })
  }

  async getConversations(query?: any): Promise<any> {
    const response = await http.get(`${this.endpoint}`, {
      params: query,
    })
    return response.data
  }

  async getConversationById(conversationId: string): Promise<any> {
    const response = await http.get(`${this.endpoint}/${conversationId}`)
    return response.data
  }

  async clearConversation(conversationId: string): Promise<any> {
    const response = await http.delete(`${this.endpoint}/${conversationId}`)
    return response.data
  }

  async blockConversation(conversationId: string): Promise<any> {
    const response = await http.post(`${this.endpoint}/${conversationId}/block`)
    return response.data
  }

  async unblockConversation(conversationId: string): Promise<any> {
    const response = await http.post(
      `${this.endpoint}/${conversationId}/unblock`
    )
    return response.data
  }

  async markConversationAsRead(conversationId: string): Promise<any> {
    const response = await http.post(`${this.endpoint}/${conversationId}/read`)
    return response.data
  }

  async getConversationSuggestions(query?: any): Promise<any> {
    const response = await http.get(`${this.endpoint}/suggestions`, {
      params: query,
    })
    return response.data
  }

  async getConversationHistory(
    conversationId: string,
    paginationParams: PaginationParams
  ): Promise<any> {
    const response = await http.get(
      `${this.endpoint}/${conversationId}/messages`,
      {
        params: { ...paginationParams, sort: paginationParams.pageSort },
      }
    )
    return response.data
  }
}

// Export singleton instance
export const ConversationsService = new ConversationsServiceCls()

// Export type for dependency injection
export type IConversationsService = ConversationsServiceCls

// Export commonly used methods directly
export const {
  createConversation,
  getConversations,
  getConversationById,
  clearConversation,
  blockConversation,
  unblockConversation,
  markConversationAsRead,
  getConversationSuggestions,
  getConversationHistory,
} = ConversationsService
